// components/cart/AvailableOffers.jsx - Updated
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaTag, FaCheckCircle, FaCopy, FaClock, FaShoppingCart } from 'react-icons/fa';
import { OfferService } from '../../api/offer.api';

const AvailableOffers = ({ onApplyOffer, cartSubtotal = 0, cartCategories = [] }) => {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [copiedCode, setCopiedCode] = useState(null);

    useEffect(() => {
        loadOffers();
    }, []);

    const loadOffers = async () => {
        try {
            setLoading(true);
            const result = await OfferService.getActiveOffers();

            if (result.success) {
                setOffers(result.data || []);
            }
        } catch (error) {
            console.error('Error loading offers:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCopyCode = (code) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);

        // Reset copied state after 2 seconds
        setTimeout(() => setCopiedCode(null), 2000);
    };

    const handleApplyOffer = async (code) => {
        if (onApplyOffer) {
            // First check if offer can be applied
            const checkResult = await OfferService.canApplyOffer(code, cartSubtotal, cartCategories);

            if (checkResult.canApply) {
                onApplyOffer(code);
            } else {
                alert(`❌ ${checkResult.reason}`);
            }
        }
    };

    if (loading) {
        return (
            <div className="p-4 text-center">
                <div className="animate-spin w-6 h-6 border-2 border-champagne border-t-transparent rounded-full mx-auto"></div>
                <p className="text-sm text-charcoal/60 mt-2">Loading offers...</p>
            </div>
        );
    }

    if (offers.length === 0) {
        return (
            <div className="p-4 bg-nude/10 rounded-xl text-center">
                <FaTag className="w-8 h-8 text-charcoal/30 mx-auto mb-2" />
                <p className="text-sm text-charcoal/60">No active offers at the moment</p>
                <p className="text-xs text-charcoal/40 mt-1">Check back soon for new promotions!</p>
            </div>
        );
    }

    // Filter and sort offers
    const applicableOffers = offers
        .filter(offer => {
            // Check minimum cart value
            if (offer.minCartValue && cartSubtotal < offer.minCartValue) {
                return false;
            }

            // Check categories if applicable
            if (offer.applicableCategories && offer.applicableCategories.length > 0) {
                const hasMatchingCategory = cartCategories.some(category =>
                    offer.applicableCategories.includes(category)
                );
                return hasMatchingCategory;
            }

            return true;
        })
        .sort((a, b) => {
            // Sort by discount value (highest first)
            return b.discountValue - a.discountValue;
        });

    const unavailableOffers = offers.filter(offer =>
        !applicableOffers.find(appOffer => appOffer._id === offer._id)
    );

    return (
        <div className="space-y-4">
            <h3 className="text-lg font-medium text-charcoal flex items-center gap-2">
                <FaTag className="w-4 h-4 text-champagne" />
                Available Offers
            </h3>

            {/* Applicable Offers */}
            {applicableOffers.length > 0 && (
                <div className="space-y-3">
                    {applicableOffers.map((offer, index) => (
                        <motion.div
                            key={offer._id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="p-4 bg-gradient-to-r from-champagne/5 to-sage/5 border border-champagne/30 rounded-xl shadow-subtle"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="relative">
                                            <span className="font-bold text-champagne text-lg">{offer.code}</span>
                                            <button
                                                onClick={() => handleCopyCode(offer.code)}
                                                className="ml-2 p-1 hover:bg-champagne/10 rounded transition-colors"
                                                title="Copy code"
                                            >
                                                <FaCopy className={`w-3 h-3 ${copiedCode === offer.code ? 'text-sage' : 'text-charcoal/60'}`} />
                                            </button>
                                            {copiedCode === offer.code && (
                                                <span className="absolute -top-6 left-0 bg-sage text-white text-xs px-2 py-1 rounded">
                                                    Copied!
                                                </span>
                                            )}
                                        </div>
                                        {offer.isSeasonal && (
                                            <span className="px-2 py-0.5 bg-gradient-to-r from-sage/20 to-champagne/20 text-sage text-xs font-medium rounded-full">
                                                Seasonal
                                            </span>
                                        )}
                                    </div>

                                    <h4 className="font-medium text-charcoal mb-1">
                                        {offer.title}
                                    </h4>

                                    <p className="text-sm text-charcoal/70 mb-3">
                                        {offer.description}
                                    </p>

                                    <div className="flex flex-wrap items-center gap-3 text-xs">
                                        {offer.minCartValue && (
                                            <div className="flex items-center gap-1 text-charcoal/60">
                                                <FaShoppingCart className="w-3 h-3" />
                                                <span>Min. ${offer.minCartValue}</span>
                                            </div>
                                        )}

                                        <div className="flex items-center gap-1 text-sage font-medium">
                                            <FaCheckCircle className="w-3 h-3" />
                                            <span>
                                                {offer.discountType === 'percentage'
                                                    ? `${offer.discountValue}% OFF`
                                                    : `$${offer.discountValue} OFF`
                                                }
                                            </span>
                                        </div>

                                        {offer.maxDiscount && (
                                            <div className="text-charcoal/50">
                                                Max discount: ${offer.maxDiscount}
                                            </div>
                                        )}
                                    </div>

                                    {offer.validTill && (
                                        <div className="mt-3 pt-3 border-t border-champagne/10">
                                            <div className="flex items-center gap-1 text-xs text-charcoal/50">
                                                <FaClock className="w-3 h-3" />
                                                <span>
                                                    Expires: {new Date(offer.validTill).toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric'
                                                })}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={() => handleApplyOffer(offer.code)}
                                    className="ml-3 px-4 py-2 bg-gradient-to-r from-champagne to-champagne/80 text-white text-sm font-medium rounded-lg hover:from-champagne/90 hover:to-champagne/70 transition-all shadow-sm hover:shadow"
                                >
                                    Apply
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Unavailable Offers (for reference) */}
            {unavailableOffers.length > 0 && (
                <div className="pt-4 border-t border-nude/30">
                    <h4 className="text-sm font-medium text-charcoal/60 mb-3">
                        Other Offers (requirements not met)
                    </h4>
                    <div className="space-y-2">
                        {unavailableOffers.map((offer, index) => (
                            <div
                                key={offer._id}
                                className="p-3 bg-nude/10 rounded-lg opacity-60"
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="font-medium text-charcoal/70">{offer.code}</span>
                                        <span className="text-sm text-charcoal/50 ml-2">{offer.title}</span>
                                    </div>
                                    <div className="text-xs text-charcoal/40">
                                        {offer.minCartValue && cartSubtotal < offer.minCartValue && (
                                            <span>Add ${(offer.minCartValue - cartSubtotal).toFixed(2)} more</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AvailableOffers;